import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FlatList,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  View,
} from "react-native";
import { useMedia } from "tamagui";
import { HORIZONTAL_CAROUSEL_ITEM_GAP } from "./constants";

type Options = {
  enableWebDragScroll?: boolean;
};

export function useHorizontalCarouselScroll<T>(
  dataLength: number,
  renderItem: ({ item }: { item: T }) => React.ReactElement,
  options: Options = {},
) {
  const { enableWebDragScroll = true } = options;
  const media = useMedia();
  const listRef = useRef<FlatList<T>>(null);

  const listWidthRef = useRef(0);
  const itemWidthRef = useRef(0);
  const contentWidthRef = useRef(0);
  const scrollXRef = useRef(0);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const renderItemRef = useRef(renderItem);
  renderItemRef.current = renderItem;

  const updateArrows = useCallback(() => {
    const x = scrollXRef.current;
    const lw = listWidthRef.current;
    const cw = contentWidthRef.current;
    setIsAtStart(x <= 5);
    setIsAtEnd(x + lw >= cw - 5);
  }, []);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      listWidthRef.current = event.nativeEvent.layout.width;
      updateArrows();
    },
    [updateArrows],
  );

  const handleContentSizeChange = useCallback(
    (w: number) => {
      contentWidthRef.current = w;
      updateArrows();
    },
    [updateArrows],
  );

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollXRef.current = event.nativeEvent.contentOffset.x;
      updateArrows();
    },
    [updateArrows],
  );

  const handleItemLayout = useCallback((event: LayoutChangeEvent) => {
    itemWidthRef.current =
      event.nativeEvent.layout.width + HORIZONTAL_CAROUSEL_ITEM_GAP;
  }, []);

  const scrollToDirection = useCallback((direction: "left" | "right") => {
    if (!listRef.current || !itemWidthRef.current) return;
    const iw = itemWidthRef.current;
    const lw = listWidthRef.current;
    const cw = contentWidthRef.current;
    let newOffset =
      direction === "right"
        ? scrollXRef.current + iw
        : scrollXRef.current - iw;
    if (newOffset < 0) newOffset = 0;
    if (newOffset > cw - lw) newOffset = cw - lw;
    listRef.current.scrollToOffset({ offset: newOffset, animated: true });
  }, []);

  const stableFlatListRenderItem = useCallback(
    ({ item, index }: { item: T; index: number }) => (
      <View onLayout={index === 0 ? handleItemLayout : undefined}>
        {renderItemRef.current({ item })}
      </View>
    ),
    [handleItemLayout],
  );

  useEffect(() => {
    if (
      !enableWebDragScroll ||
      Platform.OS !== "web" ||
      !listRef.current ||
      dataLength === 0
    )
      return;

    const flatList = listRef.current as FlatList<T> & {
      getScrollRef?: () => HTMLElement;
      _listRef?: { _scrollRef?: HTMLElement };
    };
    const scrollElement =
      flatList.getScrollRef?.() || flatList._listRef?._scrollRef;
    if (!scrollElement) return;

    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.pageX - scrollElement.offsetLeft;
      scrollLeft = scrollElement.scrollLeft;
      scrollElement.style.cursor = "grabbing";
      scrollElement.style.userSelect = "none";
    };
    const onMouseLeave = () => {
      isDragging = false;
      scrollElement.style.cursor = "grab";
    };
    const onMouseUp = () => {
      isDragging = false;
      scrollElement.style.cursor = "grab";
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      scrollElement.scrollLeft =
        scrollLeft - (e.pageX - scrollElement.offsetLeft - startX) * 2;
    };

    scrollElement.style.cursor = "grab";
    scrollElement.addEventListener("mousedown", onMouseDown);
    scrollElement.addEventListener("mouseleave", onMouseLeave);
    scrollElement.addEventListener("mouseup", onMouseUp);
    scrollElement.addEventListener("mousemove", onMouseMove);

    return () => {
      scrollElement.style.cursor = "";
      scrollElement.removeEventListener("mousedown", onMouseDown);
      scrollElement.removeEventListener("mouseleave", onMouseLeave);
      scrollElement.removeEventListener("mouseup", onMouseUp);
      scrollElement.removeEventListener("mousemove", onMouseMove);
    };
  }, [dataLength, enableWebDragScroll]);

  const buttonSize = media.gtMd ? 72 : media.gtSm ? 60 : 48;
  const iconSize = media.gtMd ? 35 : media.gtSm ? 28 : 22;
  const contentPaddingVertical = media.gtSm ? 16 : 12;

  return {
    listRef,
    isAtStart,
    isAtEnd,
    handleLayout,
    handleContentSizeChange,
    handleScroll,
    scrollToDirection,
    stableFlatListRenderItem,
    buttonSize,
    iconSize,
    itemGap: HORIZONTAL_CAROUSEL_ITEM_GAP,
    contentPaddingVertical,
  };
}
